/**
 * Schema Generator - Creates Schema.org structured data for blog posts
 * Supports Article, HowTo, FAQ, DefinedTerm, and SoftwareApplication schemas
 */

export const BLOG_CONSTANTS = {
  SITE_URL: 'https://aidatacleaner.xyz',
  SITE_NAME: 'CRM Data Cleaner',
  LOGO_URL: 'https://aidatacleaner.xyz/logo.png',
};

/**
 * Generate the post URL based on type and pillar
 * @param {Object} post - The blog post
 * @param {string} siteUrl - Base site URL
 * @param {string} pillarSlug - Parent pillar slug for cluster posts
 * @returns {string} Full URL
 */
export function generatePostUrl(post, siteUrl = BLOG_CONSTANTS.SITE_URL, pillarSlug = '') {
  if (post.type === 'pillar') {
    return `${siteUrl}/blog/${post.slug}`;
  }
  if (pillarSlug) {
    return `${siteUrl}/blog/${pillarSlug}/${post.slug}`;
  }
  return `${siteUrl}/blog/${post.slug}`;
}

/**
 * Generate Article schema node
 */
export function generateArticleNode(post, postUrl, siteUrl = BLOG_CONSTANTS.SITE_URL) {
  return {
    '@type': 'Article',
    '@id': `${postUrl}#article`,
    headline: post.title,
    description: post.excerpt || post.metaDescription || '',
    image: post.featuredImage || '',
    datePublished: post.publishedAt || new Date().toISOString(),
    dateModified: post.updatedAt || new Date().toISOString(),
    author: {
      '@type': 'Organization',
      name: BLOG_CONSTANTS.SITE_NAME,
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: BLOG_CONSTANTS.SITE_NAME,
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: BLOG_CONSTANTS.LOGO_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
  };
}

/**
 * Generate BreadcrumbList schema node
 */
export function generateBreadcrumbList(post, siteUrl = BLOG_CONSTANTS.SITE_URL, pillarTitle = '', pillarSlug = '') {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: `${siteUrl}/blog`,
    },
  ];

  // Add pillar for cluster posts
  if (post.type === 'cluster' && pillarTitle && pillarSlug) {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: pillarTitle,
      item: `${siteUrl}/blog/${pillarSlug}`,
    });
    items.push({
      '@type': 'ListItem',
      position: 4,
      name: post.title,
      item: generatePostUrl(post, siteUrl, pillarSlug),
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: post.title,
      item: generatePostUrl(post, siteUrl),
    });
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${generatePostUrl(post, siteUrl, pillarSlug)}#breadcrumb`,
    itemListElement: items,
  };
}

/**
 * Generate HowTo schema
 * @param {Object} post - The blog post
 * @param {Array} steps - Array of {name, text} objects
 */
export function generateHowToSchema(post, steps, siteUrl = BLOG_CONSTANTS.SITE_URL) {
  const postUrl = generatePostUrl(post, siteUrl);
  
  return {
    '@type': 'HowTo',
    '@id': `${postUrl}#howto`,
    name: post.title,
    description: post.excerpt || post.metaDescription || '',
    image: post.featuredImage || '',
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name || step.title || `Step ${index + 1}`,
      text: step.text || step.content || '',
      url: `${postUrl}#step-${index + 1}`,
    })),
    totalTime: `PT${Math.max(steps.length * 2, 5)}M`, // Estimate 2 min per step
  };
}

/**
 * Generate FAQ schema
 * @param {Object} post - The blog post
 * @param {Array} faqs - Array of {question, answer} objects
 */
export function generateFAQSchema(post, faqs, siteUrl = BLOG_CONSTANTS.SITE_URL) {
  const postUrl = generatePostUrl(post, siteUrl);
  
  return {
    '@type': 'FAQPage',
    '@id': `${postUrl}#faq`,
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Generate DefinedTerm schema
 * @param {Object} post - The blog post
 * @param {string} termName - The term being defined
 * @param {string} definition - The definition
 */
export function generateDefinedTermSchema(post, termName, definition, siteUrl = BLOG_CONSTANTS.SITE_URL) {
  const postUrl = generatePostUrl(post, siteUrl);
  
  return {
    '@type': 'DefinedTerm',
    '@id': `${postUrl}#definition`,
    name: termName,
    description: definition,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'CRM & Data Management Glossary',
      url: `${siteUrl}/glossary`,
    },
  };
}

/**
 * Generate SoftwareApplication schema
 * @param {Object} post - The blog post
 * @param {string} appName - Application name
 * @param {string} appDescription - Application description
 * @param {Array} features - Array of feature strings
 */
export function generateSoftwareApplicationSchema(
  post,
  appName,
  appDescription,
  features = [],
  siteUrl = BLOG_CONSTANTS.SITE_URL
) {
  const postUrl = generatePostUrl(post, siteUrl);
  
  return {
    '@type': 'SoftwareApplication',
    '@id': `${postUrl}#software`,
    name: appName,
    description: appDescription,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'CRM Data Management',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier available',
    },
    featureList: features,
    operatingSystem: 'Web Browser',
    url: postUrl,
    author: {
      '@type': 'Organization',
      name: BLOG_CONSTANTS.SITE_NAME,
      url: siteUrl,
    },
  };
}

/**
 * Generate complete schema with all selected types
 * @param {Object} post - The blog post object
 * @param {Array} selectedSchemas - Array of schema IDs to include
 * @param {Object} additionalData - Additional data for specific schemas
 * @param {string} pillarSlug - Parent pillar slug for cluster posts
 * @param {string} pillarTitle - Parent pillar title for breadcrumbs
 */
export function generateCompleteSchema(
  post,
  selectedSchemas = ['article'],
  additionalData = {},
  pillarSlug = '',
  pillarTitle = ''
) {
  const graph = [];
  const postUrl = generatePostUrl(post, BLOG_CONSTANTS.SITE_URL, pillarSlug);

  // Article (always included)
  graph.push(generateArticleNode(post, postUrl, BLOG_CONSTANTS.SITE_URL));

  // BreadcrumbList (always included)
  graph.push(generateBreadcrumbList(post, BLOG_CONSTANTS.SITE_URL, pillarTitle, pillarSlug));

  // How-To
  if (selectedSchemas.includes('howto') && additionalData.steps && additionalData.steps.length > 0) {
    graph.push(generateHowToSchema(post, additionalData.steps));
  }

  // FAQ
  if (selectedSchemas.includes('faq') && additionalData.faqs && additionalData.faqs.length > 0) {
    graph.push(generateFAQSchema(post, additionalData.faqs));
  }

  // Defined Term
  if (selectedSchemas.includes('definition') && additionalData.termName) {
    graph.push(
      generateDefinedTermSchema(
        post,
        additionalData.termName,
        additionalData.termDefinition || post.excerpt
      )
    );
  }

  // Software Application
  if (selectedSchemas.includes('software')) {
    const softwareName = additionalData.softwareName || BLOG_CONSTANTS.SITE_NAME;
    const softwareFeatures = additionalData.softwareFeatures || [];
    graph.push(
      generateSoftwareApplicationSchema(
        post,
        softwareName,
        post.excerpt || post.metaDescription || '',
        softwareFeatures
      )
    );
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/**
 * Generate basic article schema (backwards compatible)
 */
export function generateArticleSchema(post, pillarSlug = '') {
  return generateCompleteSchema(post, ['article'], {}, pillarSlug);
}

export default {
  generatePostUrl,
  generateArticleNode,
  generateBreadcrumbList,
  generateHowToSchema,
  generateFAQSchema,
  generateDefinedTermSchema,
  generateSoftwareApplicationSchema,
  generateCompleteSchema,
  generateArticleSchema,
  BLOG_CONSTANTS,
};
